import { Timestamp } from 'firebase/firestore'

export interface UserData {
  userId: string
  email: string
  name: string
  currentOrganizationId?: string
  organizationIds: string[]
  role?: 'admin' | 'user' | string
  theme?: 'light' | 'dark'
  language?: string
  profile_image_url?: string
  // Extra profile fields edited via AuthApiService.updateUserProfile.
  bio?: string
  gender?: string
  date_of_birth?: string
  permissions?: UserPermissions
  created_at?: Timestamp
  last_active_at?: Timestamp
}

export interface UserPermissions {
  chat: {
    controls: boolean
    delete: boolean
    edit: boolean
    temporary: boolean
    export: boolean
    system_prompt: boolean
    temporary_enforced: boolean
  }
  workspace: {
    models: boolean
    knowledge: boolean
    prompts: boolean
    tools: boolean
  }
  features: {
    notes: boolean
  }
}

/**
 * Every field defaults `true` except `chat.temporary_enforced`, which is
 * opt-in enforcement and must default `false`.
 */
export const DEFAULT_USER_PERMISSIONS: UserPermissions = {
  chat: {
    controls: true,
    delete: true,
    edit: true,
    temporary: true,
    export: true,
    system_prompt: true,
    temporary_enforced: false,
  },
  workspace: {
    models: true,
    knowledge: true,
    prompts: true,
    tools: true,
  },
  features: {
    notes: true,
  },
}

/**
 * Deeply-partial `UserPermissions`: a doc may omit `permissions` entirely,
 * or supply a sub-object (`chat`/`workspace`/`features`) with only some of
 * its fields set — `Partial<UserPermissions>` alone only optionalizes the
 * top level, not the fields inside each sub-object.
 */
export type PartialUserPermissions = {
  chat?: Partial<UserPermissions['chat']>
  workspace?: Partial<UserPermissions['workspace']>
  features?: Partial<UserPermissions['features']>
}

/**
 * Merges a partial permissions doc (e.g. from Firestore, which may omit the
 * whole `permissions` field or any of its sub-objects/fields) over
 * `DEFAULT_USER_PERMISSIONS`. A shallow merge per sub-object is sufficient —
 * intentionally not a generic deep-merge utility (YAGNI; the shape here is
 * small and fixed).
 */
export function resolveUserPermissions(partial?: PartialUserPermissions | null): UserPermissions {
  return {
    chat: { ...DEFAULT_USER_PERMISSIONS.chat, ...partial?.chat },
    workspace: { ...DEFAULT_USER_PERMISSIONS.workspace, ...partial?.workspace },
    features: { ...DEFAULT_USER_PERMISSIONS.features, ...partial?.features },
  }
}
